import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { supabase } from "../lib/supabase";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ArrowLeft, CheckCircle, AlertCircle, FileText } from "lucide-react";
import { AssignmentTemplateManager } from "../components/AssignmentTemplateManager";
import type { AssignmentTemplate } from "../lib/api";

interface Technique {
  id: string;
  name: string;
  category: string;
  difficulty: string;
}

export default function CreateAssignment() {
  const navigate = useNavigate();
  const { assignmentId } = useParams();
  const [searchParams] = useSearchParams();
  const classroomId = searchParams.get("classroom_id");
  const isEditMode = !!assignmentId;

  const [techniques, setTechniques] = useState<Technique[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    assignment_type: "technique_practice",
    selectedTechniques: [] as string[],
    min_problems: 20,
    min_accuracy: 80,
    due_date: "",
    publish_date: "",
    points_possible: 100,
  });

  const [selectedDifficulties, setSelectedDifficulties] = useState<string[]>(
    [],
  );

  // Template and scheduling state
  const [templateManagerOpen, setTemplateManagerOpen] = useState(false);
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [publishMode, setPublishMode] = useState<"immediate" | "scheduled">(
    "immediate",
  );

  useEffect(() => {
    if (!classroomId && !isEditMode) {
      setError("No classroom selected");
      return;
    }
    loadTechniques();
    if (isEditMode && assignmentId) {
      loadAssignment();
    }
  }, [classroomId, isEditMode, assignmentId]);

  const loadAssignment = async () => {
    try {
      const { assignments } = await api.getTeacherAssignments();
      const assignment = assignments.find((a: any) => a.id === assignmentId);

      if (!assignment) {
        setError("Assignment not found");
        return;
      }

      // Pre-fill form with existing assignment data
      setFormData({
        title: assignment.title || "",
        description: assignment.description || "",
        assignment_type: assignment.assignment_type || "technique_practice",
        selectedTechniques: assignment.content?.technique_ids || [],
        min_problems: assignment.content?.min_problems || 20,
        min_accuracy: assignment.content?.min_accuracy || 80,
        due_date: assignment.due_date
          ? new Date(assignment.due_date).toISOString().split("T")[0]
          : "",
        publish_date: "",
        points_possible: assignment.points_possible || 100,
      });
    } catch (error) {
      console.error("Failed to load assignment:", error);
      setError("Failed to load assignment");
    }
  };

  const loadTechniques = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("techniques")
        .select("id, name, category, difficulty")
        .eq("is_disabled", false)
        .order("category", { ascending: true })
        .order("name", { ascending: true });

      if (error) throw error;
      setTechniques(data || []);
    } catch (err) {
      console.error("Failed to load techniques:", err);
      setError("Failed to load techniques");
    } finally {
      setLoading(false);
    }
  };

  const toggleTechnique = (techniqueId: string) => {
    setFormData((prev) => ({
      ...prev,
      selectedTechniques: prev.selectedTechniques.includes(techniqueId)
        ? prev.selectedTechniques.filter((id) => id !== techniqueId)
        : [...prev.selectedTechniques, techniqueId],
    }));
  };

  const toggleDifficulty = (difficulty: string) => {
    setSelectedDifficulties((prev) =>
      prev.includes(difficulty)
        ? prev.filter((d) => d !== difficulty)
        : [...prev, difficulty],
    );
  };

  const handleSelectTemplate = (template: AssignmentTemplate) => {
    setFormData({
      title: template.name,
      description: template.description || "",
      assignment_type: template.assignment_type,
      selectedTechniques: template.content?.technique_ids || [],
      min_problems: template.content?.min_problems || 20,
      min_accuracy: template.content?.min_accuracy || 80,
      due_date: template.suggested_due_days
        ? new Date(
            Date.now() + template.suggested_due_days * 24 * 60 * 60 * 1000,
          )
            .toISOString()
            .split("T")[0]
        : "",
      publish_date: "",
      points_possible: template.points_possible,
    });
    setSuccess(`Template "${template.name}" loaded!`);
    setTimeout(() => setSuccess(""), 3000);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      setError("Title is required");
      return;
    }

    if (formData.selectedTechniques.length === 0) {
      setError("Please select at least one technique");
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      const content: Record<string, any> = {};

      if (formData.assignment_type === "technique_practice") {
        content.technique_ids = formData.selectedTechniques;
        content.min_problems = formData.min_problems;
        content.min_accuracy = formData.min_accuracy;
      }

      if (isEditMode && assignmentId) {
        // Update existing assignment
        await api.updateAssignment(assignmentId, {
          title: formData.title,
          description: formData.description,
          assignment_type: formData.assignment_type,
          content,
          grading_criteria: {
            accuracy_threshold: formData.min_accuracy,
            min_problems: formData.min_problems,
          },
          points_possible: formData.points_possible,
          due_date: formData.due_date || undefined,
        });

        setSuccess("Assignment updated successfully!");
        setTimeout(() => {
          navigate("/teacher");
        }, 1500);
      } else {
        // Create new assignment
        await api.createAssignment({
          classroom_id: classroomId!,
          title: formData.title,
          description: formData.description,
          assignment_type: formData.assignment_type,
          content,
          grading_criteria: {
            accuracy_threshold: formData.min_accuracy,
            min_problems: formData.min_problems,
          },
          points_possible: formData.points_possible,
          due_date: formData.due_date || undefined,
          publish_date:
            publishMode === "scheduled" && formData.publish_date
              ? formData.publish_date
              : undefined,
          auto_grade: true,
          is_published: publishMode === "immediate",
          save_as_template: saveAsTemplate,
          template_name: saveAsTemplate ? templateName || formData.title : undefined,
        });

        setSuccess("Assignment created successfully!");
        setTimeout(() => {
          navigate(`/teacher?classroom_id=${classroomId}`);
        }, 1500);
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to create assignment",
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Filter techniques by selected difficulties
  const filteredTechniques =
    selectedDifficulties.length > 0
      ? techniques.filter((t) => selectedDifficulties.includes(t.difficulty))
      : techniques;

  // Group techniques by category
  const techniquesByCategory = filteredTechniques.reduce(
    (acc, technique) => {
      if (!acc[technique.category]) {
        acc[technique.category] = [];
      }
      acc[technique.category].push(technique);
      return acc;
    },
    {} as Record<string, Technique[]>,
  );

  // Get unique difficulties for filter buttons
  const availableDifficulties = Array.from(
    new Set(techniques.map((t) => t.difficulty)),
  ).sort();

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">Loading...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <Button
        variant="ghost"
        onClick={() => navigate(`/teacher?classroom_id=${classroomId}`)}
        className="mb-6"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Classroom
      </Button>

      <Card>
        <CardHeader>
          <CardTitle>
            {isEditMode ? "Edit Assignment" : "Create New Assignment"}
          </CardTitle>
          <CardDescription>
            {isEditMode
              ? "Update the assignment details"
              : "Create a practice assignment for your students"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Use Template Button */}
            {!isEditMode && (
              <div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setTemplateManagerOpen(true)}
                  className="w-full"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Use Template
                </Button>
              </div>
            )}

            {/* Title */}
            <div>
              <Label htmlFor="title">Assignment Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                placeholder="e.g., Week 1 Practice - Fractions"
                required
              />
            </div>

            {/* Description */}
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="Brief description of the assignment"
                rows={3}
              />
            </div>

            {/* Technique Selection */}
            <div>
              <Label>Select Techniques *</Label>
              <p className="text-sm text-gray-500 mb-3">
                Choose the math techniques students should practice
              </p>

              {/* Difficulty Filters */}
              <div className="mb-4">
                <Label className="text-sm text-gray-600 mb-2 block">
                  Filter by Difficulty
                </Label>
                <div className="flex flex-wrap gap-2">
                  {availableDifficulties.map((difficulty) => (
                    <Button
                      key={difficulty}
                      type="button"
                      variant={
                        selectedDifficulties.includes(difficulty)
                          ? "default"
                          : "outline"
                      }
                      size="sm"
                      onClick={() => toggleDifficulty(difficulty)}
                      className="capitalize"
                    >
                      {difficulty}
                    </Button>
                  ))}
                  {selectedDifficulties.length > 0 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedDifficulties([])}
                      className="text-gray-500"
                    >
                      Clear Filters
                    </Button>
                  )}
                </div>
              </div>

              <div className="border rounded-lg p-4 max-h-96 overflow-y-auto space-y-4">
                {Object.entries(techniquesByCategory).map(
                  ([category, categoryTechniques]) => (
                    <div key={category}>
                      <h4 className="font-semibold text-sm text-gray-700 dark:text-gray-300 mb-2">
                        {category}
                      </h4>
                      <div className="space-y-2 ml-4">
                        {categoryTechniques.map((technique) => (
                          <div
                            key={technique.id}
                            className="flex items-center space-x-2"
                          >
                            <Checkbox
                              id={technique.id}
                              checked={formData.selectedTechniques.includes(
                                technique.id,
                              )}
                              onCheckedChange={() =>
                                toggleTechnique(technique.id)
                              }
                            />
                            <label
                              htmlFor={technique.id}
                              className="text-sm cursor-pointer flex-1"
                            >
                              {technique.name}
                              <span className="text-xs text-gray-500 ml-2">
                                ({technique.difficulty})
                              </span>
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  ),
                )}
              </div>
              <div className="flex justify-between items-center mt-2">
                <p className="text-sm text-gray-500">
                  Selected: {formData.selectedTechniques.length} technique(s)
                </p>
                <p className="text-sm text-gray-400">
                  Showing: {filteredTechniques.length} of {techniques.length}{" "}
                  techniques
                </p>
              </div>
            </div>

            {/* Requirements */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="min_problems">Minimum Problems</Label>
                <Input
                  id="min_problems"
                  type="number"
                  value={formData.min_problems}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      min_problems: parseInt(e.target.value) || 20,
                    })
                  }
                  min="1"
                />
              </div>
              <div>
                <Label htmlFor="min_accuracy">Minimum Accuracy (%)</Label>
                <Input
                  id="min_accuracy"
                  type="number"
                  value={formData.min_accuracy}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      min_accuracy: parseInt(e.target.value) || 80,
                    })
                  }
                  min="0"
                  max="100"
                />
              </div>
            </div>

            {/* Due Date and Points */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="due_date">Due Date (Optional)</Label>
                <Input
                  id="due_date"
                  type="date"
                  value={formData.due_date}
                  onChange={(e) =>
                    setFormData({ ...formData, due_date: e.target.value })
                  }
                />
              </div>
              <div>
                <Label htmlFor="points_possible">Points Possible</Label>
                <Input
                  id="points_possible"
                  type="number"
                  value={formData.points_possible}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      points_possible: parseInt(e.target.value) || 100,
                    })
                  }
                  min="1"
                />
              </div>
            </div>

            {/* Publishing Options */}
            {!isEditMode && (
              <div className="space-y-4">
                <div>
                  <Label>Publishing</Label>
                  <RadioGroup
                    value={publishMode}
                    onValueChange={(value: "immediate" | "scheduled") =>
                      setPublishMode(value)
                    }
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="immediate" id="immediate" />
                      <Label htmlFor="immediate" className="cursor-pointer">
                        Publish immediately
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="scheduled" id="scheduled" />
                      <Label htmlFor="scheduled" className="cursor-pointer">
                        Schedule for later
                      </Label>
                    </div>
                  </RadioGroup>
                </div>

                {publishMode === "scheduled" && (
                  <div>
                    <Label htmlFor="publish_date">Publish Date & Time *</Label>
                    <Input
                      id="publish_date"
                      type="datetime-local"
                      value={formData.publish_date}
                      onChange={(e) =>
                        setFormData({ ...formData, publish_date: e.target.value })
                      }
                      max={formData.due_date || undefined}
                      required={publishMode === "scheduled"}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Students won't see this assignment until the publish date
                    </p>
                  </div>
                )}

                {/* Save as Template */}
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="save_template"
                    checked={saveAsTemplate}
                    onCheckedChange={(checked) =>
                      setSaveAsTemplate(checked as boolean)
                    }
                  />
                  <Label htmlFor="save_template" className="cursor-pointer">
                    Save as template for future use
                  </Label>
                </div>

                {saveAsTemplate && (
                  <div>
                    <Label htmlFor="template_name">
                      Template Name (Optional)
                    </Label>
                    <Input
                      id="template_name"
                      value={templateName}
                      onChange={(e) => setTemplateName(e.target.value)}
                      placeholder={formData.title || "My Assignment Template"}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Error/Success Messages */}
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {success && (
              <Alert className="bg-green-50 text-green-900 border-green-200">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription>{success}</AlertDescription>
              </Alert>
            )}

            {/* Submit Button */}
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(`/teacher?classroom_id=${classroomId}`)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  submitting ||
                  !formData.title.trim() ||
                  formData.selectedTechniques.length === 0
                }
              >
                {submitting
                  ? isEditMode
                    ? "Updating..."
                    : "Creating..."
                  : isEditMode
                    ? "Update Assignment"
                    : "Create Assignment"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Template Manager Dialog */}
      <AssignmentTemplateManager
        open={templateManagerOpen}
        onOpenChange={setTemplateManagerOpen}
        onSelectTemplate={handleSelectTemplate}
      />
    </div>
  );
}
